/* eslint-disable import/prefer-default-export */
import { FieldFunctionOptions, FieldPolicy, Reference } from '@apollo/client'

import {
  Filter,
  FilterMap,
  SortParams,
  getCacheKey,
  getFilteredData,
  getFilters,
  getNewIncomingData,
  getPaginatedData,
  getSortedData,
} from '../utils'

import { PaginatedCompaniesPreviewQueryVariables } from '~generated/operations'
import { BattlecardsDocsSortKeys, CompanyObj, PaginatedCompaniesFilterInput } from '~generated/types'

type PaginatedCompaniesFieldFunction = FieldFunctionOptions<
  PaginatedCompaniesPreviewQueryVariables,
  PaginatedCompaniesPreviewQueryVariables
>

type IncomingCompanies = {
  docs: Reference[]
  totalPages: number
  totalDocs: number
  page: number
}

type ExistingCompanies = {
  [key: string]: IncomingCompanies
}

export class PaginatedCompaniesQueryCache implements FieldPolicy {
  keyArgs: false

  merge = (
    existing: ExistingCompanies,
    incoming: IncomingCompanies,
    { readField, canRead, variables }: PaginatedCompaniesFieldFunction
  ) => {
    const cacheKey = getCacheKey({ ...variables?.filter, ...variables?.sort })
    const { docs: existingDocs = [], ...restExisting } = existing?.[cacheKey] || {}
    const { docs: incomingDocs = [], ...restIncoming } = incoming

    const newIncomingDocs = getNewIncomingData({ incomingData: incomingDocs, existingData: existingDocs, readField })
    const newExistingDocs = [...existingDocs.filter(canRead), ...newIncomingDocs]

    return {
      ...existing,
      [cacheKey]: {
        ...restExisting,
        ...restIncoming,
        docs: newExistingDocs,
      },
    }
  }

  read = (existing: ExistingCompanies, options: PaginatedCompaniesFieldFunction) => {
    if (!existing) return undefined

    const { variables, readField } = options
    const { pagination, filter, sort } = variables

    const filters = getFilters(filter, this.filters)
    const cacheKey = getCacheKey({ ...filter, ...sort })
    const cachedData = existing[cacheKey]

    if (!cachedData) return undefined

    const filteredDocs = getFilteredData({
      data: cachedData.docs,
      filter,
      filters,
      readField,
    })

    const paginatedDocs = getPaginatedData({
      filteredData: filteredDocs,
      page: pagination?.page,
      limit: pagination?.limit,
    })

    // TODO - fix API to follow common sorting approach (sortKey and sortOrder)
    const sortedDocs = getSortedData({
      data: paginatedDocs,
      sortKey: sort?.docsSort || BattlecardsDocsSortKeys.NEWEST_FIRST,
      sortOrder: null,
      sortings: this.sort,
      readField,
    })

    return { ...cachedData, docs: sortedDocs }
  }

  /**
   * Sort
   */

  get sort() {
    return {
      [BattlecardsDocsSortKeys.A_Z]: this.sortAlphabeticallyASC,
      [BattlecardsDocsSortKeys.Z_A]: this.sortAlphabeticallyDESC,
      [BattlecardsDocsSortKeys.NEWEST_FIRST]: this.sortNewestFirst,
    }
  }

  // TODO - fix API to follow common sorting approach
  sortAlphabeticallyASC = ({ data, readField }: SortParams) => {
    return [...data].sort((docA, docB) => {
      const nameA = readField<string>('name', docA) || ''
      const nameB = readField<string>('name', docB) || ''
      return nameA.localeCompare(nameB)
    })
  }

  // TODO - fix API to follow common sorting approach
  sortAlphabeticallyDESC = ({ data, readField }: SortParams) => {
    return [...data].sort((docA, docB) => {
      const nameA = readField<string>('name', docA) || ''
      const nameB = readField<string>('name', docB) || ''
      return nameB.localeCompare(nameA)
    })
  }

  // TODO - fix API to follow common sorting approach
  // TODO - add createdAt and updatedAt fields to CompanyObj
  sortNewestFirst = ({ data, readField }: SortParams) => {
    return [...data].sort((docA, docB) => {
      const createdAtA = readField<number>('createdAt', docA) || 0
      const createdAtB = readField<number>('createdAt', docB) || 0
      return createdAtB - createdAtA
    })
  }

  /**
   * Filters
   */

  get filters() {
    return {
      launchId: this.launchId,
      query: this.query,
    } as FilterMap<PaginatedCompaniesFilterInput>
  }

  launchId: Filter<PaginatedCompaniesFilterInput> = (ref, filter, readField) => {
    const key: keyof CompanyObj = 'launchIds'
    const launchIds = readField(key, ref) as string[]
    return !filter.launchId || launchIds?.includes?.(filter.launchId)
  }

  query: Filter<PaginatedCompaniesFilterInput> = (ref, filter, readField) => {
    const key: keyof CompanyObj = 'name'
    const name = readField(key, ref) as string
    const searchRegExp = new RegExp(filter.query, 'i')
    return searchRegExp.test(name)
  }
}
